// ============================================================
// CalendarDayPanel.tsx
// Stage 3 of the Calendar/Diary rebuild — the click-day detail panel.
// Slides in from the right when a grid cell is clicked, in the same
// "B in Casevo" token style as the grid.
//
// Three zones:
//   1. Day items — that day's deadlines (red accent, first) + tasks
//      (green), each clickable to open its case / task / client via the
//      open handlers CalendarView already owns.
//   2. Day-notes — existing notes for the day (mine + firm-wide), with
//      add / edit / delete wired to the /api/day-notes CRUD. Each note
//      carries a private / firm-wide scope toggle. Edit/delete is gated
//      to what the backend permits (author, or admin on a firm note).
//   3. Create-on-day — "New task" / "New deadline" launch the existing
//      create flows pre-set to this day's date (the parent owns the
//      store-slot plumbing; these buttons just fire the callbacks).
//
// Close on ✕, backdrop click, or Escape — with a slide-out animation
// (the panel manages its own enter/exit transition, then calls onClose).
// Light mode only for now; dark focus-mode is Stage 4.
// ============================================================
import { BTN_PRIMARY } from '../../styles/buttons'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { CalendarEntry, DayNote, DayNoteScope, Reminder } from '../../types/calendar.types'
import { createDayNoteApi, updateDayNoteApi, deleteDayNoteApi } from '../../api/casevoApi'
import DaySummaryCard from '../shared/DaySummaryCard'
import { ErrorNote } from '../shared/ActionBox'
import { useCasevoStore } from '../../store/useCasevoStore'
import CalendarDayReminders from './CalendarDayReminders'
import { useUnsavedChangesGuard } from '../../hooks/useUnsavedChangesGuard'
import { isTimedEntry, openOutlookLink } from './CalendarTimeGrid'

interface CalendarDayPanelProps {
  date: Date // the clicked day
  entries: CalendarEntry[] // entries whose day === date (parent-filtered)
  notes: DayNote[] // day-notes whose day === date (parent-filtered)
  reminders: Reminder[] // reminders whose day === date (parent-filtered)
  onClose: () => void
  onOpenCase: (caseId: string) => void
  onOpenTask: (taskId: string) => void
  // Open an appointment for edit (appointments are listed in the panel too).
  onOpenAppointment: (appointmentId: string) => void
  // Launch the existing create flows pre-set to this day (parent sets
  // the store slots + navigates).
  onNewTask: () => void
  onNewDeadline: () => void
  // Launch the appointment form pre-set to this day (create).
  onNewAppointment: () => void
  // Fired after any note create/update/delete so the parent can refetch
  // the month and refresh the grid's 🗒 pins.
  onNotesMutated: () => void
  // Fired after any reminder mutation so the parent can refresh the
  // grid's 🔔 indicator.
  onRemindersMutated: () => void
  // Diary right-click "Add reminder" from an hour slot → pre-fill the
  // reminder composer's time ('HH:mm'). null = no pre-fill (whole-day).
  reminderInitialTime?: string | null
}

// 'YYYY-MM-DD' civil day of the clicked cell. The grid builds cell
// dates with the LOCAL Date constructor (local midnight for that civil
// day), so we read LOCAL parts here. The backend parses this as UTC-
// midnight date-only, and the grid re-keys fetched rows by their UTC
// parts — the civil integers line up, so the note lands on this day.
const isoDay = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`

// Human status label for a task entry.
const STATUS_LABEL: Record<string, string> = {
  open: 'Open',
  in_progress: 'In progress',
  done: 'Done',
}

// ── SCOPE TOGGLE ───────────────────────────────────────────
// Two-segment Private / Firm-wide control. Brand-green fill on the
// active segment; token borders throughout.
const ScopeToggle: React.FC<{
  value: DayNoteScope
  onChange: (v: DayNoteScope) => void
  disabled?: boolean
}> = ({ value, onChange, disabled }) => (
  <div className="inline-flex rounded-md border border-casevo-border overflow-hidden text-xs">
    {(['private', 'firm'] as const).map((s) => {
      const active = value === s
      return (
        <button
          key={s}
          type="button"
          disabled={disabled}
          onClick={() => onChange(s)}
          className={`px-2.5 py-1 transition-colors ${
            active
              ? 'bg-casevo-primary text-white'
              : 'bg-casevo-surface text-casevo-mid hover:text-casevo-forest hover:bg-casevo-mint/30'
          } disabled:opacity-50`}
        >
          {s === 'private' ? 'Private' : 'Firm-wide'}
        </button>
      )
    })}
  </div>
)

// Type pill per item — was a hardcoded `isDeadline ? 'Deadline' : 'Task'`, so
// every appointment (and reminder / Outlook event) was labelled TASK. Labels +
// colours mirror the calendar chips (CalendarMonthGrid Chip): appointments sky
// (court indigo), reminders amber, Outlook violet, deadlines red, tasks mint.
const APPT_LABEL: Record<string, string> = { meeting: 'Meeting', court: 'Court', call: 'Call', other: 'Appointment' }
const typePill = (entry: CalendarEntry): { label: string; cls: string } => {
  switch (entry.type) {
    case 'case_deadline':
      return { label: 'Deadline', cls: 'bg-red-100 text-red-700 dark:bg-red-500/25 dark:text-red-200' }
    case 'appointment':
      return entry.apptType === 'court'
        ? { label: 'Court', cls: 'bg-indigo-100 text-indigo-900 dark:bg-indigo-500/25 dark:text-indigo-100' }
        : { label: APPT_LABEL[entry.apptType ?? ''] ?? 'Appointment', cls: 'bg-sky-100 text-sky-900 dark:bg-sky-500/25 dark:text-sky-100' }
    case 'reminder':
      return { label: 'Reminder', cls: 'bg-amber-100 text-amber-950 dark:bg-amber-500/25 dark:text-amber-100' }
    case 'outlook':
      return { label: 'Outlook', cls: 'bg-violet-100 text-violet-900 dark:bg-violet-500/25 dark:text-violet-100' }
    default:
      return { label: 'Task', cls: 'bg-casevo-mint text-casevo-forest' }
  }
}

// Outlook row sub-line: the event's LOCAL time range ("09:30–10:15"), or
// "All day" — the same timed/all-day split the grids use (isTimedEntry).
const hm = (iso: string): string => new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
const outlookWhen = (e: CalendarEntry): string =>
  !isTimedEntry(e) ? 'All day' : e.endDate ? `${hm(e.date)}–${hm(e.endDate)}` : hm(e.date)

// ── DAY ITEM ROW ───────────────────────────────────────────
const DayItemRow: React.FC<{
  entry: CalendarEntry
  onOpen: () => void
}> = ({ entry, onOpen }) => {
  const isDeadline = entry.type === 'case_deadline'
  const pill = typePill(entry)
  return (
    <button
      type="button"
      onClick={onOpen}
      className={`w-full text-left rounded-lg border p-2.5 flex flex-col gap-1 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-casevo-mint ${
        isDeadline
          ? 'bg-red-50 border-red-200 hover:border-red-300 dark:bg-red-500/10 dark:border-red-500/40 dark:hover:border-red-500/60'
          : 'bg-casevo-mint/40 border-casevo-border hover:border-casevo-mid'
      }`}
    >
      <div className="flex items-center gap-2">
        <span className={`text-[9px] uppercase tracking-widest font-semibold px-1.5 py-0.5 rounded-full shrink-0 ${pill.cls}`}>
          {pill.label}
        </span>
        <span className="text-sm text-casevo-forest font-medium truncate">{entry.title}</span>
      </div>
      {entry.caseLabel && (
        <p className="text-xs text-casevo-mid truncate">{entry.caseLabel}</p>
      )}
      {entry.type === 'outlook' && (
        // Outlook (read-only): local time range or "All day", + location.
        <p className="text-xs text-casevo-mid truncate">
          {outlookWhen(entry)}{entry.location ? ` · ${entry.location}` : ''}
          <span className="text-casevo-text-soft"> · opens in Outlook</span>
        </p>
      )}
      {entry.type === 'task' && entry.status && (
        <span className="text-[11px] text-casevo-mid">
          {STATUS_LABEL[entry.status] ?? entry.status}
        </span>
      )}
    </button>
  )
}

const CalendarDayPanel: React.FC<CalendarDayPanelProps> = ({
  date,
  entries,
  notes,
  reminders,
  onClose,
  onOpenCase,
  onOpenTask,
  onOpenAppointment,
  onNewTask,
  onNewDeadline,
  onNewAppointment,
  onNotesMutated,
  onRemindersMutated,
  reminderInitialTime,
}) => {
  const authUser = useCasevoStore((s) => s.authUser)
  const currentUserId = authUser?.id ?? ''
  const isAdmin = authUser?.role === 'admin'
  const firmUsers = useCasevoStore((s) => s.firmUsers)

  // Respect the OS "reduce motion" setting — skip the slide animation
  // (snap open/closed) for users who ask for less movement.
  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches

  // Slide-in / slide-out animation. `shown` drives the transform; we
  // flip it true on mount (next frame) and false on close, calling
  // onClose after the transition settles. With reduced motion we skip
  // the enter delay and close immediately.
  const [shown, setShown] = useState(prefersReducedMotion)
  useEffect(() => {
    if (prefersReducedMotion) return
    const id = requestAnimationFrame(() => setShown(true))
    return () => cancelAnimationFrame(id)
  }, [prefersReducedMotion])
  // Every close (×, Esc, backdrop) asks the unsaved-changes guard first
  // (a typed note / an edited note — wired below, once the drafts exist).
  const guardRef = useRef<(proceed: () => void) => void>((proceed) => proceed())
  const handleClose = useCallback(() => {
    guardRef.current(() => {
      if (prefersReducedMotion) {
        onClose()
        return
      }
      setShown(false)
      window.setTimeout(onClose, 220)
    })
  }, [onClose, prefersReducedMotion])

  // Escape closes.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') handleClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [handleClose])

  // ── Local note list (seeded from props; kept in sync per day) ──
  const [localNotes, setLocalNotes] = useState<DayNote[]>(notes)
  useEffect(() => {
    setLocalNotes(notes)
  }, [notes])

  // Composer state.
  const [draftBody, setDraftBody] = useState('')
  const [draftScope, setDraftScope] = useState<DayNoteScope>('private')
  const [saving, setSaving] = useState(false)
  const [noteError, setNoteError] = useState<string | null>(null)

  // Per-note edit state.
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editBody, setEditBody] = useState('')
  const [editScope, setEditScope] = useState<DayNoteScope>('private')
  // Unsaved: a note typed but not added, or an edit that differs from the note.
  const editingNote = editingId ? localNotes.find((n) => n.id === editingId) : undefined
  guardRef.current = useUnsavedChangesGuard(!saving && (draftBody.trim() !== ''
    || (!!editingNote && (editBody.trim() !== editingNote.body.trim() || editScope !== editingNote.scope))))

  const dateKey = isoDay(date)

  const canManage = useCallback(
    (note: DayNote): boolean =>
      note.userId === currentUserId || (isAdmin && note.scope === 'firm'),
    [currentUserId, isAdmin],
  )

  const authorName = useCallback(
    (note: DayNote): string => {
      if (note.userId === currentUserId) return 'You'
      const u = firmUsers.find((fu) => fu.id === note.userId)
      return u ? `${u.firstName} ${u.lastName}`.trim() || 'Someone' : 'Someone'
    },
    [currentUserId, firmUsers],
  )

  const handleAdd = async (): Promise<void> => {
    const body = draftBody.trim()
    if (!body) return
    setSaving(true)
    setNoteError(null)
    try {
      const created = await createDayNoteApi({ date: dateKey, body, scope: draftScope })
      setLocalNotes((prev) => [...prev, created])
      setDraftBody('')
      setDraftScope('private')
      onNotesMutated()
    } catch (err) {
      setNoteError(err instanceof Error ? err.message : 'Failed to add note')
    } finally {
      setSaving(false)
    }
  }

  const startEdit = (note: DayNote): void => {
    setEditingId(note.id)
    setEditBody(note.body)
    setEditScope(note.scope)
    setNoteError(null)
  }
  const cancelEdit = (): void => {
    setEditingId(null)
    setEditBody('')
  }
  const handleSaveEdit = async (note: DayNote): Promise<void> => {
    const body = editBody.trim()
    if (!body) return
    setSaving(true)
    setNoteError(null)
    try {
      const updated = await updateDayNoteApi(note.id, { body, scope: editScope })
      setLocalNotes((prev) => prev.map((n) => (n.id === updated.id ? updated : n)))
      setEditingId(null)
      onNotesMutated()
    } catch (err) {
      setNoteError(err instanceof Error ? err.message : 'Failed to update note')
    } finally {
      setSaving(false)
    }
  }
  const handleDelete = async (note: DayNote): Promise<void> => {
    setSaving(true)
    setNoteError(null)
    try {
      await deleteDayNoteApi(note.id)
      setLocalNotes((prev) => prev.filter((n) => n.id !== note.id))
      if (editingId === note.id) setEditingId(null)
      onNotesMutated()
    } catch (err) {
      setNoteError(err instanceof Error ? err.message : 'Failed to delete note')
    } finally {
      setSaving(false)
    }
  }

  // Items: deadlines first, then tasks (done tasks already excluded by
  // the parent's grid filter, but guard here too for safety).
  const { deadlines, tasks } = useMemo(() => {
    const d: CalendarEntry[] = []
    const t: CalendarEntry[] = []
    for (const e of entries) {
      if (e.type === 'case_deadline') d.push(e)
      else if (!(e.type === 'task' && e.status === 'done')) t.push(e)
    }
    return { deadlines: d, tasks: t }
  }, [entries])
  // Fingerprint of this day's items for the AI summary: changes when an item
  // is added / moved / deleted, so the summary re-loads fresh (never lists a
  // deleted appointment).
  const summaryVersion = useMemo(
    () => entries.map((e) => `${e.type}:${e.appointmentId ?? e.taskId ?? e.outlookId ?? e.caseId ?? ''}:${e.date}:${e.status ?? ''}:${e.title}`).sort().join('|'),
    [entries],
  )

  // Click mapping (locked decision): a TASK always opens THAT task's
  // detail — case-linked or standalone — never the case. The task detail
  // itself shows the linked case. A DEADLINE opens its case, because a
  // deadline lives on a case and has no standalone detail of its own.
  const openEntry = (entry: CalendarEntry): void => {
    if (entry.type === 'case_deadline' && entry.caseId) {
      onOpenCase(entry.caseId)
    } else if (entry.type === 'task' && entry.taskId) {
      onOpenTask(entry.taskId)
    } else if (entry.type === 'appointment' && entry.appointmentId) {
      onOpenAppointment(entry.appointmentId)
    } else if (entry.type === 'outlook') {
      // Read-only in Casevo — the grid's only action: open it in Outlook.
      openOutlookLink(entry.webLink)
    }
  }

  // cell.date is local-midnight of the civil day, so format in local
  // time (no forced UTC) for the correct weekday / day / month.
  const heading = date.toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })

  const hasItems = deadlines.length + tasks.length > 0

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop — fixed dark scrim (theme-independent so it reads on
          both light and dark focus grounds). */}
      <div
        className={`absolute inset-0 bg-black/40 transition-opacity duration-200 motion-reduce:transition-none ${
          shown ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={handleClose}
        aria-hidden="true"
      />
      {/* Panel */}
      <div
        role="dialog"
        aria-label={`Diary for ${heading}`}
        className={`relative h-full w-full max-w-md bg-casevo-offwhite shadow-xl flex flex-col transition-transform duration-200 ease-out motion-reduce:transition-none ${
          shown ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-start justify-between px-5 py-4 border-b border-casevo-border">
          <div>
            <p className="text-xs uppercase tracking-widest text-casevo-brand mb-0.5">Diary</p>
            <h2 className="text-base font-medium text-casevo-forest">{heading}</h2>
          </div>
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close"
            className="text-casevo-mid hover:text-casevo-forest text-xl leading-none px-1 focus:outline-none casevo-focus"
          >
            ✕
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-6">
          {/* AI day summary for the opened day — compact clean surface card.
              Auto-loads (cached-or-generated) from our backend on open; the
              AI call itself happens server-side. */}
          <DaySummaryCard date={dateKey} variant="panel" dataVersion={summaryVersion} />

          {/* Create-on-day actions — full-width stacked rows so each label
              (esp. "+ New appointment") has room + padding, never cramped. */}
          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={onNewTask}
              className={`${BTN_PRIMARY} w-full !justify-start text-left whitespace-nowrap px-3 text-sm rounded-md py-2`}
            >
              + New task
            </button>
            <button
              type="button"
              onClick={onNewDeadline}
              className="w-full text-left whitespace-nowrap px-3 text-sm text-red-700 bg-red-50 border border-red-200 hover:border-red-300 dark:text-red-200 dark:bg-red-500/15 dark:border-red-500/40 dark:hover:border-red-500/60 rounded-md py-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-casevo-mint"
            >
              + New deadline
            </button>
            {/* Appointment — the DISTINCT blue family (matches the time-grid
                blocks + month chips). Opens the appointment form pre-set to
                this day. */}
            <button
              type="button"
              onClick={onNewAppointment}
              className="w-full text-left whitespace-nowrap px-3 text-sm text-sky-800 bg-sky-100 border border-sky-300 hover:border-sky-400 dark:text-sky-100 dark:bg-sky-500/20 dark:border-sky-400/50 dark:hover:border-sky-400/70 rounded-md py-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-casevo-mint"
            >
              + New appointment
            </button>
          </div>

          {/* Day items */}
          <section>
            <h3 className="text-xs uppercase tracking-widest text-casevo-mid mb-2">
              On this day
            </h3>
            {!hasItems && (
              <p className="text-sm text-casevo-mid">
                {localNotes.length === 0
                  ? 'Nothing scheduled — add a task, deadline, or note below.'
                  : 'No deadlines or tasks on this day.'}
              </p>
            )}
            <div className="flex flex-col gap-2">
              {deadlines.map((entry, i) => (
                <DayItemRow
                  key={`dl-${entry.caseId ?? ''}-${i}`}
                  entry={entry}
                  onOpen={() => openEntry(entry)}
                />
              ))}
              {tasks.map((entry, i) => (
                <DayItemRow
                  key={`tk-${entry.taskId ?? entry.caseId ?? ''}-${i}`}
                  entry={entry}
                  onOpen={() => openEntry(entry)}
                />
              ))}
            </div>
          </section>

          {/* Day notes */}
          <section>
            <h3 className="text-xs uppercase tracking-widest text-casevo-mid mb-2">Notes</h3>
            {noteError && (
              <ErrorNote className="mb-2">{noteError}</ErrorNote>
            )}
            {localNotes.length === 0 && (
              <p className="text-sm text-casevo-mid mb-3">No notes yet for this day.</p>
            )}
            <div className="flex flex-col gap-2 mb-4">
              {localNotes.map((note) => {
                const editing = editingId === note.id
                return (
                  <div
                    key={note.id}
                    className="rounded-lg border border-casevo-border bg-casevo-surface p-3"
                  >
                    {editing ? (
                      <div className="flex flex-col gap-2">
                        <textarea
                          value={editBody}
                          onChange={(e) => setEditBody(e.target.value)}
                          rows={3}
                          className="w-full border border-casevo-border rounded px-2.5 py-1.5 text-sm bg-casevo-input text-casevo-forest focus:outline-none focus:border-casevo-mid resize-y"
                        />
                        <div className="flex items-center justify-between gap-2">
                          <ScopeToggle value={editScope} onChange={setEditScope} disabled={saving} />
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={cancelEdit}
                              disabled={saving}
                              className="text-xs text-casevo-mid hover:text-casevo-forest px-2 py-1 focus:outline-none casevo-focus disabled:opacity-50"
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              onClick={() => { void handleSaveEdit(note) }}
                              disabled={saving || !editBody.trim()}
                              className="text-xs text-white bg-casevo-primary hover:bg-casevo-primary-hover rounded px-3 py-1 focus:outline-none casevo-focus disabled:opacity-50"
                            >
                              Save
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <>
                        <p className="text-sm text-casevo-forest whitespace-pre-wrap break-words">
                          {note.body}
                        </p>
                        <div className="flex items-center justify-between mt-2 gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <span
                              className={`text-[9px] uppercase tracking-widest font-semibold px-1.5 py-0.5 rounded-full shrink-0 ${
                                note.scope === 'firm'
                                  ? 'bg-casevo-mint text-casevo-forest'
                                  : 'bg-casevo-border/50 text-casevo-mid'
                              }`}
                            >
                              {note.scope === 'firm' ? 'Firm-wide' : 'Private'}
                            </span>
                            <span className="text-[11px] text-casevo-mid truncate">
                              {authorName(note)}
                            </span>
                          </div>
                          {canManage(note) && (
                            <div className="flex gap-2 shrink-0">
                              <button
                                type="button"
                                onClick={() => startEdit(note)}
                                className="text-[11px] text-casevo-mid hover:text-casevo-forest focus:outline-none casevo-focus"
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                onClick={() => { void handleDelete(note) }}
                                disabled={saving}
                                className="text-[11px] text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 focus:outline-none casevo-focus disabled:opacity-50"
                              >
                                Delete
                              </button>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )
              })}
            </div>

            {/* Composer */}
            <div className="rounded-lg border border-casevo-border bg-casevo-surface p-3 flex flex-col gap-2">
              <textarea
                value={draftBody}
                onChange={(e) => setDraftBody(e.target.value)}
                rows={3}
                placeholder="Add a note for this day…"
                className="w-full border border-casevo-border rounded px-2.5 py-1.5 text-sm bg-casevo-input text-casevo-forest placeholder:text-casevo-mid focus:outline-none focus:border-casevo-mid resize-y"
              />
              <div className="flex items-center justify-between gap-2">
                <ScopeToggle value={draftScope} onChange={setDraftScope} disabled={saving} />
                <button
                  type="button"
                  onClick={() => { void handleAdd() }}
                  disabled={saving || !draftBody.trim()}
                  className="text-sm text-white bg-casevo-primary hover:bg-casevo-primary-hover rounded-md px-4 py-1.5 transition-colors focus:outline-none casevo-focus disabled:opacity-50"
                >
                  Add note
                </button>
              </div>
            </div>
          </section>

          {/* Personal day-reminders (Stage 2 notifications). */}
          <CalendarDayReminders
            date={date}
            reminders={reminders}
            onMutated={onRemindersMutated}
            initialDraftTime={reminderInitialTime}
          />
        </div>
      </div>
    </div>
  )
}

export default CalendarDayPanel
